/**
 * usePlan — central hook to know the authenticated user's billing plan + status.
 *
 * Returns:
 *  { plan, status, periodEnd, provider, isPro, isEnterprise, hasFeature, refresh, loading }
 *
 * Plan tiers (ordered):
 *   trial < founder < startup < professional < enterprise
 *
 * Pulls from GET /api/billing/subscription. Falls back to user.plan from localStorage
 * if unauthenticated or endpoint fails.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { secureStorage } from "@/lib/secureStorage";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin) + "/api";

const TIER_ORDER = ["trial", "founder", "startup", "professional", "enterprise"];

// Feature matrix: minimum plan required to access each feature key.
// Add new features here as gates are introduced.
export const FEATURE_GATES = {
  // Free / trial
  "officers.basic": "trial",
  "intelligence_radar.basic": "trial",

  // Founder ($49)
  "pr_toolkit.basic": "founder",
  "documents.unlimited": "founder",

  // Startup ($149)
  "officers.council_mode": "startup",
  "officers.full": "startup",
  "risk_engine.predictive": "startup",
  "ngo_discount": "startup",

  // Professional ($599)
  "crisis_simulator.live_injections": "professional",
  "reputation_forecast.90d": "professional",
  "stakeholder_intelligence_map": "professional",
  "api_access": "professional",

  // Enterprise ($1499)
  "political_intelligence_engine": "enterprise",
  "brief_generator.iso22361": "enterprise",
  "sso": "enterprise",
  "audit_logs.enterprise": "enterprise",
  "dedicated_sm": "enterprise",
};

const tierIndex = (tier) => {
  const i = TIER_ORDER.indexOf(tier || "trial");
  return i === -1 ? 0 : i;
};

export const usePlan = () => {
  const [state, setState] = useState({
    plan: "trial",
    status: "none",
    periodEnd: null,
    provider: "",
    loading: true,
  });

  const refresh = useCallback(async () => {
    const token = secureStorage.getToken();
    if (!token) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    try {
      const r = await axios.get(`${API}/billing/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      });
      setState({
        plan: r.data?.plan || "trial",
        status: r.data?.subscription_status || "none",
        periodEnd: r.data?.current_period_end || null,
        provider: r.data?.provider || "",
        loading: false,
      });
    } catch (e) {
      // Network or 401 — fall back to local user
      const user = secureStorage.getUser();
      setState({
        plan: user?.plan || "trial",
        status: user?.subscription_status || "none",
        periodEnd: null,
        provider: "",
        loading: false,
      });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasFeature = useCallback(
    (featureKey) => {
      const required = FEATURE_GATES[featureKey];
      if (!required) return true; // unknown feature — allow
      return tierIndex(state.plan) >= tierIndex(required);
    },
    [state.plan]
  );

  return {
    ...state,
    isPro: tierIndex(state.plan) >= tierIndex("professional"),
    isEnterprise: state.plan === "enterprise",
    isActive: state.status === "active" || state.status === "trialing",
    isPastDue: state.status === "past_due",
    isCanceled: state.status === "canceled",
    hasFeature,
    refresh,
  };
};

export default usePlan;
